import type { Grammar, GrammarConfig, Rule } from 'graph-grammar'
import { uid } from 'graph-grammar'

// ============================================================================
// Compose several grammars into one active grammar.
//
// Grammars are treated as composable MODULES: the workbench lets the user load
// several at once and rewrites the host graph with the union of their rules.
// This helper does the merge so the loading logic (state.ts) and the UI stay
// thin. It lives in the react package on purpose , the engine's public API
// stays untouched (the prompt's "keep the engine stable" constraint).
//
// ---------------------------------------------------------------------------
// Composition policy (see also the per-section comments below):
//
//  * rules    , union of every module's rules. When more than one module is
//               loaded each rule is NAMESPACED: its `name` and `group` get the
//               module's title/key prefixed, and it is given a FRESH `id`, so
//               two modules that happen to share a rule name/group/id don't
//               clash in the rule list or in the engine. A single module is
//               left exactly as-is (no namespacing, original ids) so loading
//               one grammar is byte-for-byte the old behaviour , no regression.
//
//  * start    , the PRIMARY module supplies the start graph (axiom). The
//               primary is simply the first entry in the list; callers decide
//               ordering (the workbench keeps insertion order and treats the
//               first-loaded module as primary). Composing several rule sets
//               over a single chosen axiom is the common "modules of rules"
//               use-case; mashing several axioms together rarely makes sense.
//
//  * config   , the primary module's config wins for the qualitative knobs
//               (`strategy`, `seed`). The bound knobs (`maxSteps`, `maxNodes`)
//               take the MAX across modules so adding a module never silently
//               truncates a run that a larger module needs. `maxNodes === 0`
//               means "unbounded", so any 0 makes the merged bound unbounded.
// ============================================================================

export interface GrammarModule {
  /** Stable identifier for the module (e.g. the example `key`). */
  key: string
  /** Human-readable label used to namespace rule names/groups. */
  title: string
  grammar: Grammar
}

/** Prefix a rule `name`/`group` with its source module so modules don't clash. */
function namespaceRule(rule: Rule, mod: GrammarModule): Rule {
  return {
    ...structuredClone(rule),
    // Fresh id , two modules could share a rule id (ids are only unique within
    // a grammar). Internal LHS/RHS/morphism/embedding ids are rule-local, so
    // they never collide across rules and need no rewriting.
    id: uid('rule'),
    name: `${mod.title} · ${rule.name}`,
    // Keep rules from the same module grouped together in the rule list.
    group: rule.group ? `${mod.title} / ${rule.group}` : mod.title,
  }
}

/** Merge the bound config knobs across modules (max wins; 0 = unbounded). */
function mergeBound(values: number[]): number {
  if (values.some((v) => v === 0)) return 0
  return Math.max(0, ...values)
}

/**
 * Compose `modules` into a single active grammar.
 *
 *  - `[]`            , returns null (nothing loaded; caller decides the fallback).
 *  - one module      , returned essentially unchanged (single-grammar case).
 *  - several modules , union of namespaced rules over the primary's start/config.
 *
 * The primary module is `modules[0]`; pass the modules in the order you want,
 * with the start/config provider first.
 */
export function mergeGrammars(modules: GrammarModule[]): Grammar | null {
  if (modules.length === 0) return null

  const primary = modules[0]

  // Single module: identity (deep-cloned so callers can mutate freely). This
  // preserves the exact pre-existing "load one grammar" behaviour.
  if (modules.length === 1) return structuredClone(primary.grammar)

  const rules: Rule[] = []
  for (const mod of modules) {
    for (const r of mod.grammar.rules) rules.push(namespaceRule(r, mod))
  }

  const primaryCfg = primary.grammar.config
  const config: GrammarConfig = {
    strategy: primaryCfg.strategy,
    seed: primaryCfg.seed,
    maxSteps: mergeBound(modules.map((m) => m.grammar.config.maxSteps)),
    maxNodes: mergeBound(modules.map((m) => m.grammar.config.maxNodes)),
  }

  return {
    id: uid('gr'),
    name: modules.map((m) => m.title).join(' + '),
    rules,
    config,
    // Primary supplies the axiom (deep-cloned so edits don't leak back).
    start: structuredClone(primary.grammar.start),
  }
}
